/**
 * Turns raw Gateway transcript rows into the shapes the chat view renders.
 *
 * Transcript messages are provider-shaped and only loosely constrained by the
 * protocol (`Type.Unknown()` in `ChatHistoryDeltaResultSchema`), so everything
 * here is defensive: unknown block types degrade to nothing rather than
 * throwing, and every field is probed across the spellings the upstream
 * normalizer accepts (`src/chat/tool-content.ts`).
 */
import type { ChatAttachment } from "./attachments";
import type { Translate } from "../lib/i18n";
import { describeTool, type ToolPresentation } from "./tool-display";

export type ChatRole = "user" | "assistant" | "system" | "tool";

export type ToolActivity = {
  id: string;
  name: string;
  presentation: ToolPresentation;
  args?: unknown;
  result?: string;
  isError?: boolean;
  completed: boolean;
};

export type ChatEntry = {
  /** Stable per-entry key; falls back to a positional id for rows without one. */
  id: string;
  role: ChatRole;
  text: string;
  toolActivities: ToolActivity[];
  /** Images and files carried by this row, in either direction. */
  attachments: ChatAttachment[];
  /** Reasoning the model emitted before answering, if the provider exposes it. */
  thinking?: string;
  timestamp: number;
  /** Set while a run is still streaming into this entry. */
  streaming?: boolean;
  /** Landed while a run was in flight, so it is a step of that run's turn. */
  duringRun?: boolean;
  /** Local echo that the Gateway has not confirmed yet. */
  pending?: boolean;
  failed?: boolean;
  errorMessage?: string;
  senderLabel?: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}

/**
 * Marker for reasoning the provider withheld. The view swaps it for the
 * translated wording; keeping it a constant means the transcript layer never
 * has to know which language the interface is in.
 */
export const REDACTED_THINKING = "__redacted_thinking__";

const TOOL_CALL_TYPES = new Set(["toolcall", "tool_call", "tooluse", "tool_use"]);
const TOOL_RESULT_TYPES = new Set(["toolresult", "tool_result"]);
const IMAGE_TYPES = new Set(["image", "input_image", "image_url"]);
const THINKING_TYPES = new Set(["thinking", "reasoning"]);
const FILE_TYPES = new Set(["document", "file", "input_file"]);

function blockType(block: unknown): string {
  return isRecord(block) && typeof block.type === "string" ? block.type.toLowerCase() : "";
}

const TOOL_USE_ID_FIELDS = ["id", "tool_call_id", "toolCallId", "tool_use_id", "toolUseId"];

function readToolUseId(block: Record<string, unknown>): string | undefined {
  for (const field of TOOL_USE_ID_FIELDS) {
    const id = readString(block[field]);
    if (id) {
      return id;
    }
  }
  return undefined;
}

function readToolArgs(block: Record<string, unknown>): unknown {
  return block.args ?? block.arguments ?? block.input;
}

/** Tool results arrive as strings, block arrays, or provider-specific objects. */
function flattenToolResult(value: unknown): string {
  if (typeof value === "string") {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map(flattenToolResult).filter(Boolean).join("\n");
  }
  if (isRecord(value)) {
    const text = readString(value.text) ?? readString(value.output) ?? readString(value.content);
    if (text) {
      return text;
    }
    if (Array.isArray(value.content)) {
      return flattenToolResult(value.content);
    }
    try {
      return JSON.stringify(value);
    } catch {
      return "";
    }
  }
  return "";
}

/**
 * Reads an image or file block.
 *
 * Inline blocks carry the bytes (`data` + `mimeType`, the worker transcript
 * shape); artifact-backed ones carry only a reference — `artifactId` for
 * `artifacts.download`, or a Gateway-origin `url` — and the docs are explicit
 * that a client keeps the reference, not the bytes.
 */
function readAttachment(
  block: Record<string, unknown>,
  kind: "image" | "file",
  fallbackId: string,
): ChatAttachment | null {
  const mimeType = readString(block.mimeType) ?? readString(block.media_type);
  const source = isRecord(block.source) ? block.source : undefined;
  const data = readString(block.data) ?? readString(source?.data);
  const artifactId = readString(block.artifactId) ?? readString(block.artifact_id);
  const url = readString(block.url) ?? readString(block.mediaUrl);
  const id =
    readString(block.id) ?? artifactId ?? (url ? `${fallbackId}:${url}` : fallbackId);

  const attachment: ChatAttachment = {
    id,
    kind,
    ...(mimeType ? { mimeType } : {}),
    ...(readString(block.fileName) ?? readString(block.name)
      ? { fileName: readString(block.fileName) ?? readString(block.name) }
      : {}),
    ...(artifactId ? { artifactId } : {}),
    ...(url ? { url } : {}),
    ...(readString(block.alt) ? { alt: readString(block.alt) } : {}),
  };

  if (data) {
    // Inline bytes arrive raw or already wrapped; the view needs a URL either way.
    attachment.dataUrl = data.startsWith("data:")
      ? data
      : `data:${mimeType ?? "application/octet-stream"};base64,${data}`;
  }
  for (const [key, field] of [
    ["sizeBytes", "sizeBytes"],
    ["width", "width"],
    ["height", "height"],
  ] as const) {
    const value = block[field];
    if (typeof value === "number" && Number.isFinite(value)) {
      attachment[key] = value;
    }
  }

  return attachment.dataUrl || attachment.artifactId || attachment.url ? attachment : null;
}

export function normalizeRole(raw: unknown): ChatRole {
  const role = typeof raw === "string" ? raw.toLowerCase() : "";
  if (role === "user" || role === "human") {
    return "user";
  }
  if (role === "assistant" || role === "model") {
    return "assistant";
  }
  if (role === "tool" || role === "function" || TOOL_RESULT_TYPES.has(role)) {
    return "tool";
  }
  return "system";
}

function readTimestamp(message: Record<string, unknown>): number {
  const candidates = [message.timestamp, message.ts, message.createdAt, message.updatedAt];
  for (const candidate of candidates) {
    if (typeof candidate === "number" && Number.isFinite(candidate) && candidate > 0) {
      // Transcripts have carried both seconds and milliseconds over time.
      return candidate < 1e12 ? candidate * 1000 : candidate;
    }
  }
  return 0;
}

export type NormalizedEntry = {
  entry: ChatEntry;
  /** Tool results carried by this row, to be merged into the call that owns them. */
  toolResults: Array<{ id: string; result: string; isError: boolean }>;
};

/** Normalizes one transcript row. Returns null for rows with nothing to show. */
export function normalizeMessage(raw: unknown, fallbackId: string): NormalizedEntry | null {
  if (!isRecord(raw)) {
    return null;
  }

  const role = normalizeRole(raw.role);
  const id = readString(raw.id) ?? readString(raw.messageId) ?? fallbackId;
  const timestamp = readTimestamp(raw);
  const senderLabel = readString(raw.senderLabel);

  const textParts: string[] = [];
  const thinkingParts: string[] = [];
  const toolActivities: ToolActivity[] = [];
  const attachments: ChatAttachment[] = [];
  const toolResults: Array<{ id: string; result: string; isError: boolean }> = [];

  const content = raw.content;
  if (typeof content === "string") {
    textParts.push(content);
  } else if (Array.isArray(content)) {
    for (const block of content) {
      if (!isRecord(block)) {
        continue;
      }
      const type = blockType(block);
      if (type === "text" || (!type && typeof block.text === "string")) {
        const text = readString(block.text);
        if (text) {
          textParts.push(text);
        }
        continue;
      }
      if (THINKING_TYPES.has(type)) {
        // Redacted reasoning arrives as a marker with no text; saying so beats
        // showing an empty step.
        const thinking = readString(block.thinking) ?? readString(block.text);
        if (thinking) {
          thinkingParts.push(thinking);
        } else if (block.redacted === true) {
          thinkingParts.push(REDACTED_THINKING);
        }
        continue;
      }
      if (IMAGE_TYPES.has(type) || FILE_TYPES.has(type)) {
        const attachment = readAttachment(
          block,
          IMAGE_TYPES.has(type) ? "image" : "file",
          `${id}:media:${attachments.length}`,
        );
        if (attachment) {
          attachments.push(attachment);
        }
        continue;
      }
      if (TOOL_CALL_TYPES.has(type)) {
        const name = readString(block.name) ?? readString(block.toolName) ?? "tool";
        const args = readToolArgs(block);
        toolActivities.push({
          id: readToolUseId(block) ?? `${id}:tool:${toolActivities.length}`,
          name,
          presentation: describeTool(name, args),
          args,
          completed: false,
        });
        continue;
      }
      if (TOOL_RESULT_TYPES.has(type)) {
        const resultId =
          readToolUseId(block) ??
          readString(raw.toolCallId) ??
          readString(raw.tool_call_id) ??
          readString(raw.toolUseId) ??
          readString(raw.tool_use_id);
        if (resultId) {
          toolResults.push({
            id: resultId,
            result: flattenToolResult(block.content ?? block.output ?? block.text),
            isError: block.is_error === true || block.isError === true,
          });
        }
      }
    }
  }

  // Some producers put the visible text on the row instead of in a block.
  if (!textParts.length) {
    const text = readString(raw.text);
    if (text) {
      textParts.push(text);
    }
  }

  const text = textParts.join("\n\n").trim();
  const thinking = thinkingParts.join("\n\n").trim();

  // A bare tool-result row carries no conversation content of its own: its
  // payload is merged into the assistant turn that requested it.
  if (role === "tool" && !text && !toolActivities.length && !attachments.length && !thinking) {
    return {
      entry: { id, role, text: "", toolActivities: [], attachments: [], timestamp },
      toolResults,
    };
  }
  if (!text && !toolActivities.length && !attachments.length && !thinking) {
    return null;
  }

  return {
    entry: {
      id,
      role,
      text,
      toolActivities,
      attachments,
      ...(thinking ? { thinking } : {}),
      timestamp,
      ...(senderLabel ? { senderLabel } : {}),
    },
    toolResults,
  };
}

/**
 * Normalizes a whole transcript, attaching each tool result to the call that
 * produced it so the UI can render one activity chip per tool instead of a
 * request/response pair.
 */
export function normalizeTranscript(messages: readonly unknown[]): ChatEntry[] {
  const entries: ChatEntry[] = [];
  const callsById = new Map<string, ToolActivity>();

  messages.forEach((raw, index) => {
    const normalized = normalizeMessage(raw, `entry:${index}`);
    if (!normalized) {
      return;
    }
    for (const activity of normalized.entry.toolActivities) {
      callsById.set(activity.id, activity);
    }
    for (const result of normalized.toolResults) {
      const target = callsById.get(result.id);
      if (target) {
        target.result = result.result;
        target.isError = result.isError;
        target.completed = true;
      }
    }
    const { entry } = normalized;
    if (entry.text || entry.toolActivities.length || entry.attachments.length || entry.thinking) {
      entries.push(entry);
    }
  });

  // A transcript page arrives oldest-first; keep that order stable for rows that
  // share a timestamp rather than letting an unstable sort reshuffle them.
  return entries;
}

/** Short one-line preview for the conversation list. */
export function previewText(entry: ChatEntry | undefined, t: Translate): string {
  if (!entry) {
    return "";
  }
  if (entry.text) {
    return entry.text.replace(/\s+/g, " ").trim();
  }
  const attachment = entry.attachments.at(-1);
  if (attachment) {
    return attachment.kind === "image"
      ? t("attachment.previewImage")
      : t("attachment.previewFile", { name: attachment.fileName ?? t("attachment.file") });
  }
  const activity = entry.toolActivities.at(-1);
  if (activity) {
    return `${activity.presentation.emoji} ${activity.presentation.title}`;
  }
  return "";
}
